/**
 * Explicit, measure-and-inject SDA print pagination (raytd/web#157).
 *
 * raytd/web#153 measured (against the real 28-item staging report via
 * `tools/print-pagination-harness`, not a hand-built fixture) that Chromium's `printToPDF`
 * simply does not honour `break-inside: avoid` for an SDA tile that starts too close to a
 * page's bottom to fit whole — confirmed across CSS Grid, a real `rowSpan` table, a two-row
 * table, a single-row table, and every `break-inside`/`break-after`/`page-break-*` combination
 * tried on any of those shapes. Total split count never moved; only which item split did.
 *
 * This module replaces asking Chromium's fragmentation engine to decide at all: given each
 * `.printable-tile-wrapper`'s real rendered height (measured live, see the `beforeprint` glue
 * that calls this — not here, this file is DOM-free on purpose) and the page's usable content
 * height, decide up front which items need an explicit forced break (the existing, previously
 * unused `.page-break` class — `break-before: page` — in print.css) immediately before them so
 * they always start fresh at the top of a new page with the full budget available. Chromium is
 * then never asked to fragment an item mid-page — it never gets the chance to be wrong about it.
 *
 * A single item taller than the whole page budget still splits inside itself — this only fixes
 * MIS-PLACED breaks between items. `findOversizeIndices` picks those items out so the caller can
 * give each its own repeating column-heading header (QA 108.02, raytd/web#147): Chromium's split
 * leaves the continuation page with no headings otherwise.
 */

/**
 * SDA prints on `@page report-page-landscape` (print.css): A4 landscape (297mm × 210mm) minus a
 * 1cm top margin and a 1.5cm bottom margin, leaving a 185mm-tall usable content area (documented,
 * exact figure, next to `.print-section-appendix` in print.css — not empirically guessed).
 *
 * CSS defines 1in = 96px and 1in = 25.4mm, so the mm-to-px conversion below is a fixed constant
 * of the CSS spec, not a physical-DPI-dependent guess — valid PROVIDED the height measurement
 * this budget is compared against is taken while the browser is actually laid out at print
 * media / the page's real content width (see the `beforeprint` glue — that's exactly when this
 * holds; measuring against the screen viewport instead would make this budget meaningless).
 */
const SDA_PRINT_PAGE_CONTENT_HEIGHT_MM = 185;
const CSS_PX_PER_MM = 96 / 25.4;
export const SDA_PRINT_PAGE_BUDGET_PX = SDA_PRINT_PAGE_CONTENT_HEIGHT_MM * CSS_PX_PER_MM;

/**
 * Given each pagination unit's real rendered height (document order) and the usable page-height
 * budget, return the indices that need an explicit page break inserted immediately before them.
 *
 * Pure decision logic, no DOM — mirrors `pagination_probe.py`'s separation of pure
 * placement-matching logic from browser/PDF glue, which worked well for #152/#153.
 *
 * Algorithm: walk items in order, tracking cumulative height on the "current" simulated page.
 * Before any item whose height would push that total past the budget, mark a break before it
 * and reset the running total to that item's own height (it starts the new page). The first
 * item never gets a spurious leading break — there's no page above it to break from, and
 * inserting one would waste an entire blank page.
 *
 * An item taller than the whole budget on its own is never itself skipped past: it still starts
 * a fresh page (so it gets the full budget available to it), but it will still overflow that
 * page and split within itself — the documented, accepted limitation this module doesn't solve.
 */
export function computePageBreakIndices(
    heights: readonly number[],
    budgetPx: number = SDA_PRINT_PAGE_BUDGET_PX,
    // Page 1 can have less room than later pages (QA 108.02: the "Results" section title sits above
    // its tiles). Defaults to `budgetPx` so existing callers are unchanged.
    firstPageBudgetPx: number = budgetPx
): number[] {
    const breakIndices: number[] = [];
    let currentPageHeight = 0;
    let pageBudgetPx = firstPageBudgetPx;

    heights.forEach((height, index) => {
        if (index === 0) {
            currentPageHeight = height;
            return;
        }

        const wouldOverflow = currentPageHeight + height > pageBudgetPx;
        if (wouldOverflow) {
            breakIndices.push(index);
            pageBudgetPx = budgetPx;
            currentPageHeight = height;
        } else {
            currentPageHeight += height;
        }
    });

    return breakIndices;
}

/**
 * Indices of items strictly taller than the page budget — the ones Chromium will fragment
 * mid-item however the breaks between items are placed. Pure, like `computePageBreakIndices`.
 */
export function findOversizeIndices(
    heights: readonly number[],
    budgetPx: number = SDA_PRINT_PAGE_BUDGET_PX
): number[] {
    const indices: number[] = [];
    heights.forEach((height, index) => {
        if (height > budgetPx) indices.push(index);
    });
    return indices;
}
